import { HttpErrorResponse } from '@angular/common/http';
import { HttpTestingController } from '@angular/common/http/testing';
import { ComponentFixture } from '@angular/core/testing';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { FileToShare, FileTypes } from '@dntlab/share-files-dialog';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { SessionService } from '../auth/session.service';
import { FileCategories } from '../components/files/files-manager.enum';
import { AggregatedItemTypes, MapFilters } from '../components/map/map.enum';
import { MarkersStateChanges } from '../components/map/map.interfaces';
import { OrganizationComponent } from '../components/profile/user-profile/edit-profile/user-type/organization/organization.component';
import { PhoneFormFieldComponent } from '../forms/fields/components/phone-form-field/phone-form-field.component';
import { SingleDropdownFormFieldComponent } from '../forms/fields/components/single-dropdown-form-field/single-dropdown-form-field.component';
import FieldAllowedValueClasses from '../forms/fields/field-allowed-value-classes.values';
import { FieldAttributeConstructorService } from '../forms/fields/field-attributes-constructor.service';
import { FormConstructorBaseComponent } from '../forms/form-constructors/form-constructor-base/form-constructor-base.component';
import { FormConstructorProfileBaseComponent } from '../forms/form-constructors/form-constructor-profile-base/form-constructor-profile-base.component';
import { FormValidationService } from '../forms/form-validation.service';
import { MapMarkers } from '../models/map-markers.model';
import { RoleInfo } from '../models/role-info.model';
import { DialogIndividualRoleSelectComponent } from '../notifications/dialogs/dialog-individual-role-select/dialog-individual-role-select.component';
import {
  CategoryBasicView,
  ChannelKind,
  EmptyResponse,
  FileDescriptor,
  FoldersResponseInner,
  ListOfBasicUsers,
  LoginRequestAccount,
  Post,
  RootComment,
  SearchResponse,
  SearchResult,
  SearchResultAggregation,
  SecurityDescriptor,
  Statistics,
} from '../swagger-api';
import { CountryInfo, PhoneNumberInfo } from './country-info.interface';
import { DialogSelectRoleData } from './dialog.interface';
import { FileSharedEvent, ImageDimension } from './file.interface';
import { Attribute, SelectorOption } from './form-and-field.interface';
import {
  AggregatedStorageItem,
  CovidStatistics,
  MapGotoBlockForm,
  MapLocation,
  MapSearchBlockForm,
  MapState,
  OverallStatistics,
  ProcessMapResponseContent,
  ProcessMapSearchResponseStrategy,
} from './map.interface';
import { PaginationState } from './pagination.interface';
import { AddCommentContent } from './posts.interface';
import { QueryUrlParams } from './query-url-params.interface';
import { SearchParameters, SearchRequest } from './search.interface';

export interface TestError {
  errorDescriptionId: string;
  fieldName: string;
}

export interface TestRouterLinks {
  dataTestId: string;
  expectedLink: string;
  content: string;
  isAuthNav?: boolean;
}

export interface TestRegistrationUser {
  email: string;
  accountType: string;
  alias: string;
  organizationName: string;
  password: string;
  repeatPassword: string;
  agree: boolean;
  agreeCheck?: boolean;
}

export interface PasswordRecoveryPasswords {
  password: string;
  repeatPassword: string;
}

export interface LoginRequestAccountWithErrorText extends LoginRequestAccount {
  errorText: string;
}

export interface RepeatPasswordsTest {
  data: TestRegistrationUser;
  errors: TestError[];
}

export interface AcceptSignupInvitationFormData {
  alias: string;
  password: string;
  repeatPassword: string;
  agree: boolean;
}

export interface TestAcceptSignupInvitationRequest extends AcceptSignupInvitationFormData {
  token: string;
}

export interface AcceptSignupInvitationPasswordsTest {
  data: AcceptSignupInvitationFormData;
  errors: TestError[];
}

export interface TestChangePasword {
  currentPassword: string;
  password: string;
  repeatPassword: string;
}

export interface ChangePasswordsTest {
  data: TestChangePasword;
  errors: TestError[];
}

export interface TestChangePasswordWithErrorText {
  currentPassword: string;
  errorText: string;
}

export interface FindValidatorData {
  service: FieldAttributeConstructorService;
  attr: Attribute;
  validator: ValidatorFn;
  isFound: boolean;
}

export interface TestFormValidationContent {
  service: FormValidationService;
  control: UntypedFormControl;
  isInvalid: boolean;
  expectedError: string;
}

export interface TestCheckValidityContent {
  component: FormConstructorBaseComponent;
  controlName: string;
  errorType: string;
  errorText: string;
  hasError: boolean;
}

export interface TestFCOnSubmitContent {
  fixture: ComponentFixture<FormConstructorProfileBaseComponent>;
  data: object;
  selector: string;
  isFormValid: boolean;
  hasSelector: boolean;
}

export interface TestExpectedNotEmptySetupStartValueContent {
  component: PhoneFormFieldComponent;
  controlValue: string;
  callCountryInfo: CountryInfo;
  callCountryPhoneInfo: PhoneNumberInfo;
  phoneNumber: string;
}

export interface ExpectationOnSelectedCountryContent {
  component: PhoneFormFieldComponent;
  callCountryInfo: CountryInfo;
  callCountryPhoneInfo: PhoneNumberInfo;
}

export interface TestOrganizationFormSubmitContent {
  fixture: ComponentFixture<OrganizationComponent>;
  isFormsValid: boolean;
}

export interface TestFormSubmitContent<T> {
  fixture: ComponentFixture<T>;
  data: SecondAuthLvlContent;
  selectedChannel: ChannelKind;
  isFormsValid: boolean;
  hasInitialValue: boolean;
  bindAuthenticator?: boolean;
}

export interface SecondAuthLvlContent {
  code?: string;
  phone_number?: string;
  channelKind: ChannelKind;
  params?: QueryUrlParams;
}

export interface InitPhoneControlContent<T> {
  fixture: ComponentFixture<T>;
  attrService: FieldAttributeConstructorService;
  phoneNumber: string;
  email?: string;
  isEmpty: boolean;
}

export interface RoleSelectDialogInitContent {
  fixture: ComponentFixture<DialogIndividualRoleSelectComponent>;
  modalArguments: DialogSelectRoleData;
  hasNotSavedRole: boolean;
  selectedRoleValue?: string;
}

export interface SpyOnSaveRoleBthClickContent {
  fixture: ComponentFixture<DialogIndividualRoleSelectComponent>;
  rolesInfo: RoleInfo[];
}

export interface SessionAuthExpectationData {
  httpMock?: HttpTestingController;
  sessionService: SessionService;
  method?: Observable<string>;
  url: string;
  type: string;
  isAuth: boolean;
}

export interface InvalidDataContentFC {
  values: object;
  invalidControlName: string;
  errorType: string;
  errorText: string;
}

export interface AddRemoveCommentTestContent {
  title: string;
  shouldBeAddedRemoved: boolean;
  result: Observable<RootComment | never>;
  commentsLength: number;
  dialogResponse?: boolean;
}

export interface TestPaginationI {
  offset: number;
  limit: number;
}

export interface AddRemoveMemberToEntityContent {
  title: string;
  result: Observable<EmptyResponse | never>;
  error: HttpErrorResponse;
  open?: boolean;
  isLeader?: boolean;
  pagination: TestPaginationI;
}

export interface TestSearchResultVisibilityExpect {
  id: number;
  isVisible: boolean;
}

export interface PostDIServiceTestContent {
  title: string;
  result?: Observable<Post>;
  success?: boolean;
  info?: AddCommentContent;
}

export interface HasInvalidFileSizeOrDimensionsTest {
  dimensions?: ImageDimension;
  size?: number;
  type?: string;
  invalid: boolean;
}

export interface ProgressBarTest {
  values: number[];
  className: string;
}

export interface ValidationInputFieldExpect {
  testedValue: string;
  expectedValue: string;
  isValid: boolean;
}

export interface OnCheckErrorsExpect {
  fixture: ComponentFixture<SingleDropdownFormFieldComponent>;
  testedValue: string;
  expectedControlValue: SelectorOption | string;
  expectedSelectedItemValue: SelectorOption | string;
  dataTestId: string;
  errors?: string[];
  suggestions: SelectorOption[];
  editable?: boolean;
  event: NgbTypeaheadSelectItemEvent | undefined;
  validateFieldCalledValue: string;
}

export interface RangeContent {
  first: number;
  second: number;
  value?: number;
}

export interface ChangeMarkersContent {
  currentState: MapState;
  previousState: MapState;
  wasFirstMethodCalled?: boolean;
  noMethodsWereCalled?: boolean;
  notHandleRequest?: boolean;
}

export interface MapStateCheckContent {
  location: MapLocation;
  zoom: number;
  wasFirstMethodCalled?: boolean;
}

export interface MapStateTestContent {
  response: SearchResult[];
  expectedMarkersChanges: MarkersStateChanges;
  totalMatchesCount: number;
  filter: MapFilters;
  description: string;
}

export interface SearchResultTotalCountTestContent {
  title: string;
  totalMatchesCount: number;
  searchResultTotalCount: string;
}

export interface SearchResultsToCustomMarkersAdapterContent extends ProcessMapResponseContent {
  prototype?: ProcessMapSearchResponseStrategy;
  expectedMarkersChanges: MarkersStateChanges;
}

export interface ProcessMarkersTestI {
  content: ProcessMapResponseContent;
  originalMarkers: MapMarkers;
  expectedMarkers: MapMarkers;
  description: string;
}

export interface HasValidFileExtensionExpectContent {
  name: string;
  valid: boolean;
  type?: string;
}

export interface FileTypeFromNameTest {
  type: FileTypes;
  fileName: string;
}

export interface Check2ndAuthLevelGuardTest {
  isLoggedIn: boolean;
  is2AuthLvlPassed?: boolean;
  expectedValue: boolean;
}

export interface SecondLevelGuardGetPathTest {
  activatedRouteSnapshot: object;
  expectedResult: string;
  testSuite: number;
}

export interface CheckAdminSessionAuthGuardTest {
  isLoggedIn: boolean;
  isAdminSession: boolean;
}

export interface CheckUpdateBlockerTimerTest {
  currentDate: Date;
  blockedUntilDate: Date;
  shouldStopTimer: boolean;
}

export interface CountdownTimerPhoneTest {
  value: number;
  expectedResult: boolean;
}

export interface SearchResultsTest {
  expectedCount: number;
  expectedSelectedCategoryHint?: string;
  expectedObjectToTranslate: (string | object)[];
}

export interface ShareFilesTest {
  files: FoldersResponseInner[];
  allUsers: ListOfBasicUsers;
  currentUserId: string;
  filesToShare?: FileToShare[];
  title?: string;
}

export interface OnShareFilesResponse {
  response: string | boolean | FileToShare[];
  expectedResult?: FileSharedEvent[];
}

export interface ProcessedNonAggregatedMarkerExpectI {
  result: SearchResult;
  specialization: CategoryBasicView[];
  name?: string;
  image?: string;
  role?: string;
  url: string;
  includeStreet?: boolean;
}

export interface ProcessedAggregatedMarkerExpectI {
  kind: AggregatedItemTypes;
  response: SearchResultAggregation;
  url: string;
  color: string;
}

export interface GetCommunitiesProjectsExpectSO {
  serviceName: string;
  initMethodName: string;
  notifyMethodName: string;
  onNotifyMethodName: string;
  controlName: string;
  valueClass: FieldAllowedValueClasses;
  propName?: string;
  isLinkedShow: boolean;
  objects: object;
  expectedObjects: SelectorOption[];
}

export interface GetSearchResultsExpectI {
  params: SearchParameters;
  callNext?: boolean;
  error?: boolean;
  pagination?: PaginationState;
  calledValues?: SearchRequest;
  searchResponse?: SearchResponse;
  results?: SearchResult[];
}

export interface TestCounterPreset {
  imagesCount: number;
  symbolsCount: number;
  htmlString: string;
  status: string;
}

export interface OnSearchFormSubmittedTest {
  isLocationPreSelected?: boolean;
  incomingFormData: MapSearchBlockForm | MapGotoBlockForm;
  expectedMapCurrentState?: MapState;
  expectedComponentState?: MapState;
  flagsCheck?: boolean;
  numeric?: number;
  zoom?: number;
}

export interface FetchStatisticTest extends FetchStatisticNestedTest {
  methodName: string;
  serviceName: string;
  serviceMethodName: string;
  response: Observable<Statistics | CovidStatistics>;
  expectedStatistics: OverallStatistics;
}

export interface FetchStatisticNestedTest {
  initialStorage?: Map<string, OverallStatistics>;
  emitStatisticsFromStorage?: boolean;
  initialTempStatistics?: OverallStatistics;
  emitTempStatistics?: boolean;
  callServiceMethod?: boolean;
  isSubmitted?: boolean;
  currentState?: MapState;
  previousState?: MapState;
  saveStatisticsToStorage?: boolean;
}

export interface ProxyProcessSearchResponseI {
  isFormSubmitted?: boolean;
  isAggregated?: boolean;
  wasSavedToStorage?: boolean;
  filter?: MapFilters;
  searchBlockForm?: MapSearchBlockForm;
  zoom?: number;
  aggregatedItemsStorageKey?: string;
  element?: number;
}

export interface ProxyFetchAggregatedItemsI {
  wasFetchFromStorageHandlerCalled?: boolean;
  wasFetchAggregatedItemsCalled?: boolean;
  wasEmitMapMarkersCalled?: boolean;
  wasNoRefreshTimeExceededCaseProcessed?: boolean;
  wasNoRefreshTimeExceeded?: boolean;
  currentState: MapState;
  previousState?: MapState;
  storage?: Map<string, AggregatedStorageItem>;
  text: string;
  wasProcessSearchResponseCalled?: boolean;
}

export interface MembersTabPaginationStateTestI {
  offset: number;
  pageNumber: number;
}

export interface FileDataItem {
  name?: string;
  isDirectory?: boolean;
  size?: number;
  dataItem?: DataItem;
  dateModified?: string;
  hasSubDirectories?: boolean;
  path?: string;
  thumbnail?: string;
  key?: string;
}

export interface DataItem {
  item?: Item;
  members?: string;
}

export interface Item {
  entity_kind?: string;
  entity?: FileDescriptor;
  security?: SecurityDescriptor[];
  content_type?: FileCategories;
}
